import { useEffect, useMemo, useRef, useState } from "react";
import { de } from "date-fns/locale";
import { addDays, format } from "date-fns";
import { CalendarCheck, Loader2, ArrowLeft, Download, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { useUI } from "@/context/UIContext";
import { http, API, errorText, newToken, whatsappLink } from "@/lib/api";

const GROUPS = [["Nacht", 0, 6], ["Vormittag", 6, 12], ["Nachmittag", 12, 18], ["Abend", 18, 24]];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function SlotPicker({ day, slot, setSlot }) {
  const [slots, setSlots] = useState(null);
  const [err, setErr] = useState("");
  const [group, setGroup] = useState(null);
  useEffect(() => {
    let alive = true;
    setSlots(null); setErr("");
    http.get("/slots", { params: { date: format(day, "yyyy-MM-dd") } })
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data?.slots) ? data.slots : [];
        setSlots(list);
        const first = list.find((s) => s.available);
        const h = first ? parseInt(first.label_full, 10) : 9;
        setGroup(GROUPS.findIndex(([, a, b]) => h >= a && h < b));
      })
      .catch((e) => alive && setErr(errorText(e, "Termine konnten nicht geladen werden.")));
    return () => { alive = false; };
  }, [day]);
  if (err) return <p role="alert" className="text-[14px] text-[#B42318]">{err}</p>;
  if (!slots) return <div className="flex items-center gap-2 text-[14px] text-mute"><Loader2 className="h-4 w-4 animate-spin" /> Freie Zeiten werden geladen …</div>;
  const shown = slots.filter((s) => { const h = parseInt(s.label_full, 10); return h >= GROUPS[group][1] && h < GROUPS[group][2]; });
  return (
    <div>
      <div role="tablist" aria-label="Tageszeit" className="grid grid-cols-4 gap-1 rounded-full border border-line bg-white p-1">
        {GROUPS.map(([l], i) => (
          <button key={l} role="tab" type="button" aria-selected={group === i} data-testid={`booking-group-${i}`} onClick={() => setGroup(i)}
            className={`rounded-full py-2 text-[12px] font-semibold transition-colors sm:text-[13px] ${group === i ? "bg-ink text-paper" : "text-ink"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-3 grid max-h-[260px] grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-4" data-testid="booking-slot-grid">
        {shown.map((s) => (
          <button key={s.start} type="button" disabled={!s.available} onClick={() => setSlot(s)} data-testid={`booking-slot-${s.label_full.replace(/\W/g, "")}`}
            aria-pressed={slot?.start === s.start} aria-label={`${s.label_full} Uhr${s.available ? "" : " – nicht verfügbar"}`}
            className={`h-11 rounded-lg border text-[14px] font-semibold tabular-nums transition-colors ${slot?.start === s.start ? "border-ink bg-ink text-paper" : s.available ? "border-line bg-white hover:border-ink" : "cursor-not-allowed border-transparent bg-black/[0.04] text-black/30 line-through"}`}>
            {s.label_full}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function BookingDialog() {
  const { bookingOpen, setBookingOpen } = useUI();
  const [day, setDay] = useState(new Date());
  const [slot, setSlot] = useState(null);
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ name: "", email: "", phone: "", topic: "", hp: "" });
  const [errs, setErrs] = useState({});
  const [sending, setSending] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const [result, setResult] = useState(null);
  const token = useRef(newToken());
  const today = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), []);

  useEffect(() => {
    if (bookingOpen && result) { setResult(null); setStep(1); setSlot(null); token.current = newToken(); }
  }, [bookingOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const v = {};
    if (f.name.trim().length < 2) v.name = "Bitte geben Sie Ihren Namen an.";
    if (!EMAIL_RE.test(f.email.trim())) v.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    if (!/^[+0-9 ()/-]{6,30}$/.test(f.phone.trim())) v.phone = "Bitte geben Sie eine gültige Rückrufnummer an.";
    setErrs(v);
    if (Object.keys(v).length) return;
    setSending(true); setServerErr("");
    try {
      const { data } = await http.post("/bookings", { start: slot.start, ...f, topic: f.topic || null, client_token: token.current });
      if (!data?.id) throw new Error("not stored");
      setResult(data); setStep(3);
    } catch (err) {
      const msg = errorText(err, "Die Buchung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.");
      setServerErr(msg);
      if (err?.response?.status === 409) { token.current = newToken(); }
    } finally { setSending(false); }
  };

  const inp = (k, extra = {}) => (
    <input id={`book-${k}`} value={f[k]} onChange={set(k)} data-testid={`booking-input-${k}`} aria-invalid={!!errs[k]} {...extra}
      className={`h-12 w-full rounded-xl border bg-white px-4 text-[15px] outline-none focus:border-ink focus:ring-2 focus:ring-gold/40 ${errs[k] ? "border-[#B42318]" : "border-line"}`} />
  );
  const icsUrl = result ? `${API}/bookings/${result.id}/ics?token=${encodeURIComponent(result.token)}` : "";
  const manageUrl = result ? `/termin?id=${result.id}&token=${encodeURIComponent(result.token)}` : "";

  return (
    <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
      <DialogContent data-testid="booking-dialog" className="max-h-[100dvh] w-full max-w-[760px] overflow-y-auto rounded-none border-line bg-paper p-0 sm:max-h-[94vh] sm:rounded-3xl">
        <div className="p-6 sm:p-9">
          <p className="eyebrow text-gold-deep">Telefonisches Erstgespräch · 15 Minuten</p>
          <DialogTitle className="mt-3 font-display text-[26px] font-semibold tracking-tight sm:text-[32px]">{step === 3 ? "Ihr Termin ist gebucht." : "Termin auswählen"}</DialogTitle>
          <DialogDescription className="mt-1 text-[14px] text-mute">Alle Zeiten in <strong className="text-ink">Europe/Vienna</strong> (Wiener Zeit). Täglich verfügbar, auch am Wochenende.</DialogDescription>

          {step === 1 && (
            <div className="mt-6 grid gap-6 md:grid-cols-[auto_1fr]">
              <Calendar mode="single" selected={day} onSelect={(d) => { if (d) { setDay(d); setSlot(null); } }} locale={de} weekStartsOn={1}
                disabled={[{ before: today }, { after: addDays(today, 60) }]} className="w-fit rounded-2xl border border-line bg-white" data-testid="booking-calendar" />
              <div>
                <p className="mb-3 text-[14px] font-semibold">{format(day, "EEEE, d. MMMM yyyy", { locale: de })}</p>
                <SlotPicker day={day} slot={slot} setSlot={setSlot} />
                <button type="button" disabled={!slot} onClick={() => setStep(2)} data-testid="booking-continue" className="btn btn-ink mt-5 w-full">
                  {slot ? `Weiter mit ${slot.label_full} Uhr` : "Bitte eine Uhrzeit wählen"}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={submit} noValidate className="mt-6 space-y-4">
              <button type="button" onClick={() => setStep(1)} data-testid="booking-back" className="flex items-center gap-2 text-[14px] font-semibold"><ArrowLeft className="h-4 w-4" /> {format(day, "EEE, d. MMM", { locale: de })} · {slot?.label_full} Uhr ({slot?.tz_label}) ändern</button>
              {[["name", "Name", { autoComplete: "name", maxLength: 80 }], ["email", "E-Mail", { type: "email", autoComplete: "email" }], ["phone", "Rückrufnummer", { type: "tel", autoComplete: "tel", maxLength: 30 }]].map(([k, l, ex]) => (
                <div key={k}><label htmlFor={`book-${k}`} className="mb-1.5 block text-[14px] font-semibold">{l}</label>{inp(k, ex)}{errs[k] && <p role="alert" className="mt-1 text-[13px] text-[#B42318]">{errs[k]}</p>}</div>
              ))}
              <div><label htmlFor="book-topic" className="mb-1.5 flex justify-between text-[14px] font-semibold">Thema <span className="font-normal text-mute">optional</span></label>
                <textarea id="book-topic" rows={2} maxLength={500} value={f.topic} onChange={set("topic")} data-testid="booking-input-topic" className="w-full rounded-xl border border-line bg-white px-4 py-3 text-[15px] outline-none focus:border-ink" /></div>
              <input aria-hidden="true" tabIndex={-1} autoComplete="off" value={f.hp} onChange={set("hp")} className="absolute -left-[9999px] h-0 w-0" />
              {serverErr && (
                <div role="alert" data-testid="booking-error" className="flex gap-3 rounded-xl border border-[#B42318]/30 bg-[#FEF3F2] p-4 text-[14px] text-[#7A271A]">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{serverErr} Alternativ: <a className="font-semibold underline" href={whatsappLink()} target="_blank" rel="noopener noreferrer">WhatsApp</a>.</span>
                </div>
              )}
              <p className="text-[12px] text-mute">Ihre Angaben dienen nur der Terminvereinbarung. <a href="/datenschutz" target="_blank" className="underline">Datenschutz</a></p>
              <button type="submit" disabled={sending} data-testid="booking-submit" className="btn btn-ink w-full !min-h-[54px]">
                {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Wird gespeichert …</> : "Termin verbindlich buchen"}
              </button>
            </form>
          )}

          {step === 3 && result && (
            <div className="mt-6" data-testid="booking-success">
              <div className="flex items-start gap-4 rounded-2xl border border-line bg-white p-5">
                <CalendarCheck className="mt-1 h-6 w-6 text-gold-deep" />
                <div>
                  <p className="font-display text-[20px] font-semibold">{result.local_date}</p>
                  <p className="text-[15px]">{result.local_time} Uhr ({result.tz_label}) · Europe/Vienna · 15 Minuten</p>
                  <p className="mt-2 text-[14px] text-mute">Wir rufen Sie unter Ihrer angegebenen Nummer an. Die Buchungsdetails senden wir an Ihre E-Mail-Adresse.</p>
                </div>
              </div>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <a href={icsUrl} data-testid="booking-ics-download" className="btn btn-ink"><Download className="h-4 w-4" /> In Kalender übernehmen (.ics)</a>
                <a href={manageUrl} data-testid="booking-manage-link" className="btn btn-line">Termin verwalten oder stornieren</a>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
